<template>
  <div class="payment-info">
    <h5 class="payment-info__title">Payment Information</h5>

    <!-- Product -->
    <div class="payment-info__block">
      <h6>Product</h6>
      <p>
        {{ product.name }}
        <span v-if="!hasOptions && product.quantity_enabled && quantity > 1" class="text-muted">&times;{{ quantity }}</span>
      </p>
    </div>

    <div
      v-if="application"
      class="payment-info__block"
    >
      <h6>Application selection</h6>
      <p>
        <span>{{ application.user_full_name }}</span>&nbsp;-&nbsp;<span>{{ application.program_name }}</span>
      </p>
    </div>

    <!-- Selected options -->
    <div v-if="selectedOptions.length > 0" class="payment-info__block">
      <h6>Selected Options</h6>
      <p
        v-for="opt in selectedOptions"
        :key="opt.id"
        class="d-flex justify-content-between"
      >
        <span>
          {{ opt.label }}
          <span v-if="opt.quantity > 1" class="text-muted">&times;{{ opt.quantity }}</span>
        </span>
        <span v-if="optionLineTotal(opt)">{{ optionLineTotal(opt) | fixAmount }}</span>
      </p>
    </div>

    <!-- Custom amount -->
    <div v-if="Number(customAmount) > 0" class="payment-info__block">
      <h6>{{ hasOptions ? 'Additional Custom Amount' : 'Amount' }}</h6>
      <p>{{ Number(customAmount) | fixAmount }}</p>
    </div>

    <!-- Subtotal -->
    <div class="payment-info__block">
      <h6>Subtotal</h6>
      <p>{{ subtotal | fixAmount }}</p>
    </div>

    <!-- Payment method -->
    <div class="payment-info__block">
      <h6>Payment Method</h6>
      <p>{{ paymentMethod }}</p>
    </div>

    <!-- Card info (Credit Card only) -->
    <div v-if="paymentMethod === 'Credit Card' && card" class="payment-info__block">
      <h6>Payment Card</h6>
      <p class="d-flex align-items-center">
        <VisaSVG v-if="card.type && card.type.toLowerCase() === 'visa'" class="card-icon" />
        <MastercardSVG v-if="card.type && card.type.toLowerCase() === 'mastercard'" class="card-icon" />
        <AchSVG v-if="card.type && card.type.toLowerCase() === 'ach'" class="card-icon" />
        <span>xxxx {{ card.last_digits }}</span>
      </p>
    </div>

    <!-- Payment fees -->
    <div v-if="applicableFees.length > 0" class="payment-info__block">
      <h6>Payment Fees</h6>
      <p
        v-for="fee in applicableFees"
        :key="fee.id"
        class="d-flex justify-content-between"
      >
        <span>{{ fee.name }}</span>
        <span>{{ fee.calculatedAmount | fixAmount }}</span>
      </p>
    </div>

    <!-- Grand total -->
    <div class="d-flex justify-content-between bg-light p-3 rounded-lg">
      <span>Total:</span>
      <span class="font-weight-bold">{{ totalWithFees | fixAmount }}</span>
    </div>
  </div>
</template>

<script>
import { filters } from '@core/mixins/filters'
import { mapGetters } from 'vuex'
import { calculateApplicableFees } from '@/utils/payment'
import VisaSVG from '@/assets/images/icons/visa.svg'
import MastercardSVG from '@/assets/images/icons/mastercard.svg'
import AchSVG from '@/assets/images/icons/ach.svg'

export default {
  name: 'ProductCheckoutPaymentInfo',
  components: {
    VisaSVG,
    MastercardSVG,
    AchSVG,
  },
  mixins: [filters],
  props: {
    product: {
      type: Object,
      required: true,
    },
    selectedOptions: {
      type: Array,
      default: () => [],
    },
    customAmount: {
      type: Number,
      default: null,
    },
    hasOptions: {
      type: Boolean,
      default: false,
    },
    /** Quantity selected for a simple (no-options) quantity-enabled product */
    quantity: {
      type: Number,
      default: 1,
    },
    /** Options sub-total + custom amount, before fees */
    subtotal: {
      type: Number,
      required: true,
    },
    paymentMethod: {
      type: String,
      default: 'Credit Card',
    },
    /** Full card object from cards store */
    card: {
      type: Object,
      default: null,
    },
    paymentFees: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters({
      application: 'paymentPortal/getSelectedApplication',
    }),
    /** Resolve ACH cards to their correct fee bucket */
    effectivePaymentMethod() {
      if (this.paymentMethod === 'Credit Card' && this.card) {
        return this.card.type?.toLowerCase() === 'ach' ? 'ACH' : 'Credit Card'
      }
      return this.paymentMethod
    },
    applicableFees() {
      return calculateApplicableFees(this.paymentFees, this.effectivePaymentMethod, this.subtotal)
    },
    totalFeeAmount() {
      return this.applicableFees.reduce((sum, f) => sum + f.calculatedAmount, 0)
    },
    totalWithFees() {
      return Math.round((Number(this.subtotal) + this.totalFeeAmount) * 100) / 100
    },
  },
  methods: {
    /**
     * Line total for a selected option.
     * Prefers the pre-computed `line_total` (fixed = unit × qty, custom = entered total)
     * sent by ProductCheckout.vue; falls back to `amount` for backward compatibility.
     */
    optionLineTotal(opt) {
      if (opt.line_total != null) return Number(opt.line_total)
      return Number(opt.amount) || 0
    },
  },
}
</script>

<style lang="scss" scoped>

</style>

